import React, { useEffect, useState } from "react";
import { useRouteMatch, useParams, useHistory } from "react-router-dom";
import classNames from "classnames";
import {
  useGetTermsAndPpQuery,
  useGetActivationFlowTermsAndPpQuery,
  useGetUnifiedPortalFeatureFlagQuery,
} from "common/types/mainHooks";
import { type ArenaObjectType } from "common/types/mainTypes";
import { useAppContext } from "apps/portal/Portal/common/AppContext";
import { NotFoundPage } from "apps/portal/Portal/common/NotFoundPage";
import { Text, TypeStyle } from "@jsq/ds/deprecated";
import { Grid, Row, Col } from "react-bootstrap";
import { SideNav, Tabs, Tab, Card, QueryLoader } from "@jsq/ds/core";
import {
  getLpPortalTermsAndPrivacyPath,
  getLpPortalTermsAndPrivacyPathNoNav,
  getUnifiedPortalTouBasePath,
} from "common/helpers/url";
import { PAGE_TITLES } from "../constants";
import { useSetDocumentTitle } from "../DocumentTitle";
import { PrivacyFields } from "./PrivacyFields";
import { generateLeftHandSideList, generatePrivacyPolicies } from "./utils";
import { type ArenaItem } from "./types";
import { jsqTermsOfUse } from "./constants";
import styles from "./styles.scss";

export type ManagerArena = Pick<
  ArenaObjectType,
  "id" | "name" | "domain" | "clientTos" | "clientPrivacyPolicy"
>;

type Props = {
  isAuthenticated: boolean;
  token?: string;
  singleArena?: boolean;
  noNav?: boolean;
};

export const TermsAndPolicy: React.FunctionComponent<Props> = ({
  isAuthenticated,
  token,
  singleArena,
  noNav,
}) => {
  const [isTermsOfUseDisplayed, setIsTermsOfUseDisplayed] = useState(true);
  const featureFlagResult = useGetUnifiedPortalFeatureFlagQuery({
    context: { unauthenticated: true },
  });

  const { managerArenas } = useAppContext();
  const history = useHistory();
  const code = token || "";
  const params = useParams<{ arena: string; arenaDomain?: string }>();
  let arenaIds;
  if (singleArena && params.arenaDomain) {
    arenaIds = managerArenas
      .filter((arena) => params.arenaDomain?.includes(arena.secondLevelDomain))
      .map((arena) => arena.id);
  } else {
    arenaIds = managerArenas.map((arena) => arena.id);
  }

  const showNav = !noNav;

  const unAuthArenaResult = useGetActivationFlowTermsAndPpQuery({
    variables: { code },
    context: { unauthenticated: true },
    skip: isAuthenticated || code.length === 0, // If no token provided, still render JSQ TOS/PP
  });
  const authArenaResult = useGetTermsAndPpQuery({
    variables: { arenaIds },
    skip: !isAuthenticated,
  });

  let arenasList: ArenaItem[] = [];
  let isUnifiedPortal = false;
  const unAuthQueryType = unAuthArenaResult.data?.unifiedPortalUnauthTermsAndPolicy.__typename;

  if (isAuthenticated) {
    isUnifiedPortal = managerArenas.length > 0;
    arenasList =
      !authArenaResult.loading && authArenaResult.data?.managerArenas
        ? authArenaResult.data.managerArenas
        : [];
  } else {
    arenasList =
      !unAuthArenaResult.loading &&
      unAuthQueryType === "UnifiedPortalUnauthTermsAndPolicyObject" &&
      unAuthArenaResult.data?.unifiedPortalUnauthTermsAndPolicy.gpArena
        ? [unAuthArenaResult.data?.unifiedPortalUnauthTermsAndPolicy.gpArena]
        : [];
    isUnifiedPortal = arenasList.length > 0;

    arenaIds = arenasList.filter((a) => a.clientPrivacyPolicy || a.clientTos).map((a) => a.id);
  }

  const routeMatch = useRouteMatch();
  const arena = params?.arena || "jsq";

  const { origin, hostname } = window.location;

  const jsqTou = jsqTermsOfUse(hostname, origin, code, isUnifiedPortal);
  const termsAndConditionsDocuments = generatePrivacyPolicies({
    arenasList,
    jsqTou,
  });

  const isTermsOfUseExists =
    !unAuthArenaResult.loading &&
    termsAndConditionsDocuments[arena] &&
    termsAndConditionsDocuments[arena].tou !== "";
  const isPrivacyPolicyExists =
    !unAuthArenaResult.loading &&
    termsAndConditionsDocuments[arena] &&
    termsAndConditionsDocuments[arena].pp !== "";
  const queryParam = code.length > 0 ? `?activation=${code}` : "";

  const touUrl = routeMatch.url.endsWith("/") ? "tou" : "/tou";
  const ppUrl = routeMatch.url.endsWith("/") ? "pp" : "/pp";

  useSetDocumentTitle(PAGE_TITLES.TermsAndPrivacy);

  useEffect(() => {
    if (history.location.pathname === `${routeMatch.url}${ppUrl}` || !isTermsOfUseExists) {
      setIsTermsOfUseDisplayed(false);
    } else {
      setIsTermsOfUseDisplayed(true);
    }
  }, [history.location.pathname, routeMatch.url, isTermsOfUseExists, ppUrl]);

  useEffect(() => {
    // If arena is JSQ or query data is loading, no need to perform any route checks
    // since we are guaranteed a TOU/PP for JSQ.
    if (
      unAuthArenaResult.loading ||
      authArenaResult.loading ||
      featureFlagResult.loading ||
      arena === "jsq"
    ) {
      return;
    }

    let touPath;
    if (isAuthenticated) {
      touPath = singleArena
        ? getLpPortalTermsAndPrivacyPathNoNav(params.arenaDomain)
        : getLpPortalTermsAndPrivacyPath();
    } else {
      touPath = getUnifiedPortalTouBasePath();
    }

    // If user refreshes after deleting both TOS/PP, redirect to JSQ.
    if (!termsAndConditionsDocuments[arena]) {
      history.push(`${touPath}${queryParam}`);
    }
    const isPPUrl = history.location.pathname === `${routeMatch.url}${ppUrl}`;
    const isTOUUrl =
      history.location.pathname === `${routeMatch.url}${touUrl}` ||
      history.location.pathname === `${routeMatch.url}`;

    // Scenario: user 1 is on PP page, user 2 deletes the PP async, user 1 refreshes,
    // We should redirect to TOU page if it exist, otherwise redirect to JSQ page.
    // The "else if" is the same logic applied for TOU page and it gets deleted.
    if (isPPUrl && !isPrivacyPolicyExists) {
      if (isTermsOfUseExists) {
        history.push(`${routeMatch.url}${touUrl}${queryParam}`);
      } else {
        history.push(`${touPath}${queryParam}`);
      }
    } else if (isTOUUrl && !isTermsOfUseExists) {
      if (isPrivacyPolicyExists) {
        history.push(`${routeMatch.url}${ppUrl}${queryParam}`);
      } else {
        history.push(`${touPath}${queryParam}`);
      }
    }
  }, [
    isPrivacyPolicyExists,
    isTermsOfUseExists,
    history,
    routeMatch.url,
    unAuthArenaResult.loading,
    queryParam,
    arena,
    termsAndConditionsDocuments,
    ppUrl,
    touUrl,
    authArenaResult.loading,
    featureFlagResult.loading,
    isAuthenticated,
    singleArena,
    params.arenaDomain,
  ]);
  const sideNavItems = generateLeftHandSideList(
    arenasList,
    isAuthenticated,
    code,
    arena,
    showNav,
    params.arenaDomain,
  );

  if (
    unAuthArenaResult.data?.unifiedPortalUnauthTermsAndPolicy.__typename ===
      "UnifiedPortalUnauthTermsAndPolicyError" &&
    unAuthArenaResult.data.unifiedPortalUnauthTermsAndPolicy.errorCode ===
      "JSQGraphQLErrorCode.GLOBAL_PORTAL_INVALID_INVITE_TOKEN"
  ) {
    // we only need to block unauth view for invalid tokens
    // realistically all expected errors should be caught prior to coming to this page,
    // either on the activation or login pages
    return <NotFoundPage />;
  }

  return (
    <QueryLoader
      result={
        isAuthenticated
          ? [authArenaResult, featureFlagResult]
          : [unAuthArenaResult, featureFlagResult]
      }
    >
      <Grid className={styles.TermsAndPolicy__container}>
        <Row className={styles.TermsAndPolicy__header}>
          <Col md={10}>
            <div className={styles.TermsAndPolicy__title}>
              <Text typeStyle={TypeStyle.Display} data-cy="terms-and-policy-title">
                Terms and privacy
              </Text>
            </div>
          </Col>
        </Row>
        <Row className={styles.TermsAndPolicy_contentContainer}>
          {sideNavItems.length > 1 && (
            <Col md={3} className={styles.TermsAndPolicy__leftHandSide}>
              <SideNav sideNavItems={sideNavItems} />
            </Col>
          )}
          <Col md={sideNavItems.length > 1 ? 9 : 12}>
            <Card variant="white" className={styles.TermsAndPolicy__tabsCard}>
              <Row className={styles.TermsAndPolicy__tabsContainer}>
                <Tabs tabsListClassName={styles.TermsAndPolicy__tabsList}>
                  {isTermsOfUseExists ? (
                    <Tab
                      active={isTermsOfUseDisplayed}
                      text="Terms of Use"
                      to={`${routeMatch.url}${touUrl}${queryParam}`}
                      onClick={() => setIsTermsOfUseDisplayed(true)}
                      data-cy="termsOfUseTab"
                    />
                  ) : null}
                  {isPrivacyPolicyExists ? (
                    <Tab
                      active={!isTermsOfUseDisplayed}
                      text="Privacy Policy"
                      to={`${routeMatch.url}${ppUrl}${queryParam}`}
                      onClick={() => setIsTermsOfUseDisplayed(false)}
                      data-cy="privacyPolicyTab"
                    />
                  ) : null}
                </Tabs>
              </Row>
            </Card>
            {termsAndConditionsDocuments[arena] && (
              <Card
                variant="white"
                className={classNames(
                  styles.TermsAndPolicy__contextCard,
                  !isAuthenticated ? styles.TermsAndPolicy__contextCard__unauthorized : null,
                )}
                data-cy="terms-privacy-context-card"
              >
                <Row className={styles.TermsAndPolicy__context}>
                  {isTermsOfUseDisplayed && isTermsOfUseExists ? (
                    <PrivacyFields
                      arena={arena}
                      privacyType="tou"
                      termsAndConditionsDocuments={termsAndConditionsDocuments}
                    />
                  ) : (
                    <PrivacyFields
                      arena={arena}
                      privacyType="pp"
                      termsAndConditionsDocuments={termsAndConditionsDocuments}
                    />
                  )}
                </Row>
              </Card>
            )}
          </Col>
        </Row>
      </Grid>
    </QueryLoader>
  );
};

export default TermsAndPolicy;
